import React from 'react';
import { Link } from 'react-router-dom';
import { Fish, CheckCircle, FileText, ArrowRight, Wind } from 'lucide-react';

const SeafoodProcessing = () => {
  const emissionSources = [
    {
      source: 'Fish Drying Operations',
      pollutants: 'Volatile Organic Compounds (VOCs), Particulate Matter',
      impact: 'Can adversely affect air quality and worker health if not properly controlled',
    },
    {
      source: 'Power Generation',
      pollutants: 'NOx, SO2, CO, Particulate Matter',
      impact: 'Emissions from diesel generators and boilers require monitoring and control',
    },
    {
      source: 'Smoking and Curing',
      pollutants: 'Particulate Matter, VOCs, Odors',
      impact: 'Traditional preservation methods generate emissions requiring management',
    },
  ];

  const controlMeasures = [
    'Installation of advanced filtration systems',
    'Continuous emission monitoring systems (CEMS)',
    'Regular maintenance and optimization of equipment',
    'Implementation of Best Available Control Technology (BACT)',
    'Worker exposure monitoring and ventilation improvements',
    'Odor control technologies',
  ];

  return (
    <div className="min-h-screen" data-testid="seafood-page">
      {/* Hero Section */}
      <section
        className="hero-section relative text-white overflow-hidden"
        style={{
          height: '60vh',
          maxHeight: '720px',
          minHeight: '700px',
          padding: '5rem 0'
        }}
      >
        {/* Responsive Hero Image Container */}
        <div className="absolute inset-0 z-0">
          <style>{`
            .hero-section picture {
              width: 100%;
              height: 100%;
              display: block;
            }
            
            .hero-section .hero-image,
            .hero-section picture img {
              width: 100% !important;
              height: 100% !important;
              object-fit: cover !important;
              object-position: center 60% !important;
              display: block !important;
            }
            
            @media (max-width: 900px) {
              .hero-section {
                height: auto !important;
                max-height: none !important;
                min-height: 420px !important;
                padding: 3rem 0 !important;
              }
              .hero-section picture img {
                object-position: center center !important;
              }
            }
          `}</style>
          
          <picture>
            {/* Mobile Vertical */}
            <source
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/awp1m154_seafood_hero_mobile_vertical_960x1200.webp"
              media="(max-width: 480px)"
              type="image/webp" />

            {/* Mobile Horizontal */}
            <source
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/6bc3ltcs_seafood_hero_mobile_horizontal_960x640.webp"
              media="(max-width: 767px)"
              type="image/webp" />

            {/* Tablet */}
            <source
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/jmbzm9ek_seafood_hero_tablet_1536x900.webp"
              media="(max-width: 1279px)"
              type="image/webp" />

            {/* Desktop */}
            <source
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/wpk3kr7e_seafood_hero_desktop_2560x1200.webp"
              media="(min-width: 1280px)"
              type="image/webp" />

            {/* Fallback Image */}
            <img
              src="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/wpk3kr7e_seafood_hero_desktop_2560x1200.webp"
              alt="Alaska seafood processing plant with fishing vessel docked at sunset, representing environmental compliance services for Alaska's seafood industry."
              loading="eager"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 60%' }}
              className="w-full h-full" />
          </picture>
          
          {/* Dark Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/50 to-transparent"></div>
        </div>
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10 h-full flex items-center">
          <div className="max-w-3xl">
            <div className="flex items-center mb-6">
              <Fish className="w-16 h-16 mr-4" />
              <div>
                <h1 className="text-4xl md:text-5xl font-bold">
                  Seafood Processing Plants
                </h1>
                <p className="text-xl mt-2">Air Quality Management & Compliance</p>
              </div>
            </div>
            <p className="text-xl md:text-2xl max-w-3xl">
              Air quality is a critical consideration in seafood processing, ensuring compliance and protecting worker health.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Alaska's Seafood Industry</h2>
            <p className="text-lg text-gray-600 mb-4 leading-relaxed">
              Air quality is a critical consideration in seafood processing plants, particularly for operations involving fish drying and power generation. The processes emit various pollutants that must be effectively managed to ensure compliance with environmental regulations and protect worker health.
            </p>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Alaska's seafood processing industry is vital to the state's economy, and Raven Environmental Consulting understands the unique challenges faced by facilities operating in remote coastal locations with seasonal variations in production.
            </p>
            
            <div className="bg-cyan-50 p-6 rounded-lg border-l-4 border-cyan-600">
              <h3 className="font-bold text-gray-900 mb-3">Small Power Plant Definition</h3>
              <p className="text-gray-700">
                Small power plants used for seafood processing are defined in 18 AAC 50.400(i)(10) as Title V sources containing diesel-fired internal combustion engines to generate power, with potential to emit less than 500 tons per year of regulated air pollutants in the aggregate.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Emission Sources */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Key Emission Sources</h2>
          <div className="space-y-6 max-w-5xl mx-auto">
            {emissionSources.map((source, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-lg border-l-4 border-cyan-600">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{source.source}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Pollutants</h4>
                    <p className="text-gray-600">{source.pollutants}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Impact</h4>
                    <p className="text-gray-600">{source.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regulatory Requirements */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Federal Regulatory Requirements</h2>
          <div className="space-y-8 max-w-5xl mx-auto">
            {/* NSPS */}
            <div className="bg-gray-50 p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">40 CFR 60 - New Source Performance Standards</h3>
              <p className="text-gray-600 mb-4">Seafood processing facilities must comply with various NSPS subparts:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Subpart IIII</h4>
                  <p className="text-sm text-gray-600">Compression ignition internal combustion engines for power generation</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Subpart Dc</h4>
                  <p className="text-sm text-gray-600">Small industrial-commercial-institutional steam generating units</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Subpart JJJJ</h4>
                  <p className="text-sm text-gray-600">Stationary spark ignition internal combustion engines</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Subpart A</h4>
                  <p className="text-sm text-gray-600">General provisions for all NSPS sources</p>
                </div>
              </div>
            </div>

            {/* NESHAP */}
            <div className="bg-gray-50 p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">40 CFR 63 - NESHAP Requirements</h3>
              <p className="text-gray-600 mb-4">Hazardous Air Pollutant standards for seafood processing:</p>
              <div className="space-y-3">
                <div className="flex items-start bg-white p-4 rounded-lg">
                  <Wind className="w-5 h-5 text-cyan-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Subpart ZZZZ - Stationary RICE</h4>
                    <p className="text-sm text-gray-600">Emission standards for reciprocating internal combustion engines at major and area sources</p>
                  </div>
                </div>
                <div className="flex items-start bg-white p-4 rounded-lg">
                  <Wind className="w-5 h-5 text-cyan-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Subpart JJJJJJ - Industrial Boilers</h4>
                    <p className="text-sm text-gray-600">Standards for industrial, commercial, and institutional boilers at area sources</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Alaska Specific */}
            <div className="bg-white p-8 rounded-lg shadow-lg border-l-4 border-cyan-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Alaska-Specific Requirements</h3>
              <p className="text-gray-600 mb-4">
                Unique regulatory considerations for Alaska seafood processing:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-cyan-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Small power plant permits for seasonal operations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-cyan-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Remote location operational flexibility provisions</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-cyan-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Diesel fuel quality requirements</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-cyan-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Emergency generator provisions</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Control Measures */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Air Quality Control Measures</h2>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <p className="text-gray-600 mb-6 leading-relaxed">
                Effective management of air quality in seafood processing requires implementation of comprehensive control measures:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {controlMeasures.map((measure, index) => (
                  <div key={index} className="flex items-start bg-gray-50 p-4 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-cyan-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{measure}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Expertise */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-cyan-50 p-8 rounded-lg max-w-4xl mx-auto border-2 border-cyan-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Specialized Seafood Industry Expertise</h2>
            <p className="text-lg text-gray-700 text-center leading-relaxed mb-4">
              Raven Environmental Consulting provides specialized support for Alaska's seafood processing industry, understanding the unique challenges of seasonal operations, remote locations, and the need for reliable power generation.
            </p>
            <p className="text-lg text-gray-700 text-center leading-relaxed">
              Our team ensures your facility maintains compliance while supporting the productivity and efficiency essential to Alaska's vital seafood industry.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-cyan-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ensure Compliance for Your Seafood Processing Facility</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Our expertise in seafood processing air quality ensures your operations meet all regulatory requirements while protecting worker health.
          </p>
          <Link
            to="/contact/seafood-processing"
            className="inline-flex items-center px-8 py-4 bg-white text-cyan-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
          >
            Contact Our Seafood Industry Specialists
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SeafoodProcessing;
